/**
 * frontend/onboarding/ArtistPickStep.jsx
 * SoundDAO — Artist Pick onboarding step for Players.
 *
 * Renders immediately after persona selection + reward moment for Player persona,
 * before routing to PlayerDashboard.
 *
 * Two tabs:
 *   Tab 1 — "Artists on Big Creative" — pick an existing creator to recruit
 *   Tab 2 — "Invite your favorite" — nominate an off-platform artist + generate invite link
 *
 * Props:
 *   userId     — current user's UUID
 *   onComplete — callback({ recruited?, nominated? }) → navigates to PlayerDashboard
 */

import React, { useState, useEffect, useCallback } from 'react';
import InviteArtistPanel from '../components/InviteArtistPanel';
import { authFetch } from '../lib/authFetch';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ── Styles (inline to keep single-file) ───────────────────────────────────────
const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
    color: '#fff',
    padding: '24px 16px 100px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    textAlign: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    margin: '0 0 6px',
    color: '#ffffff',
  },
  subtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.75)',
    margin: 0,
  },
  tabRow: {
    display: 'flex',
    gap: 0,
    marginBottom: 20,
    borderRadius: 12,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  tab: (active) => ({
    flex: 1,
    padding: '12px 8px',
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'center',
    cursor: 'pointer',
    border: 'none',
    background: active ? 'rgba(102,126,234,0.3)' : 'rgba(255,255,255,0.05)',
    color: active ? '#fff' : 'rgba(255,255,255,0.5)',
    transition: 'all 0.2s',
  }),
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
    marginBottom: 20,
  },
  card: (selected) => ({
    background: selected ? 'rgba(102,126,234,0.2)' : 'rgba(255,255,255,0.05)',
    border: selected ? '2px solid #667eea' : '2px solid transparent',
    borderRadius: 14,
    padding: 14,
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
  }),
  avatar: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    objectFit: 'cover',
    margin: '0 auto 8px',
    display: 'block',
    background: 'rgba(102,126,234,0.3)',
  },
  initialsCircle: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 8px',
    fontSize: 20,
    fontWeight: 700,
  },
  cardName: {
    fontSize: 14,
    fontWeight: 600,
    margin: '0 0 4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardMeta: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.65)',
    margin: '0 0 2px',
  },
  stageBadge: {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: 20,
    background: 'rgba(240,147,251,0.2)',
    color: '#f093fb',
    marginTop: 4,
  },
  fanCount: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.65)',
    marginTop: 4,
  },
  platformRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
  },
  pill: (active) => ({
    flex: 1,
    padding: '10px 0',
    borderRadius: 20,
    border: active ? '2px solid #667eea' : '2px solid rgba(255,255,255,0.15)',
    background: active ? 'rgba(102,126,234,0.2)' : 'transparent',
    color: active ? '#fff' : 'rgba(255,255,255,0.5)',
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'center',
    cursor: 'pointer',
  }),
  input: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(255,255,255,0.05)',
    color: '#fff',
    fontSize: 15,
    outline: 'none',
    marginBottom: 16,
    boxSizing: 'border-box',
  },
  resultCard: {
    background: 'rgba(102,126,234,0.1)',
    border: '1px solid rgba(102,126,234,0.3)',
    borderRadius: 14,
    padding: 16,
    marginBottom: 16,
  },
  resultUrl: {
    fontSize: 12,
    wordBreak: 'break-all',
    color: 'rgba(255, 255, 255, 0.75)',
    marginBottom: 12,
  },
  resultButtons: {
    display: 'flex',
    gap: 8,
  },
  resultBtn: {
    flex: 1,
    padding: '10px 0',
    borderRadius: 10,
    border: 'none',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
  },
  copyBtn: {
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
  },
  dmBtn: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
  },
  continueBtn: (enabled) => ({
    width: '100%',
    padding: '16px 0',
    borderRadius: 14,
    border: 'none',
    background: enabled ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'rgba(255,255,255,0.08)',
    color: enabled ? '#fff' : 'rgba(255,255,255,0.3)',
    fontSize: 16,
    fontWeight: 700,
    cursor: enabled ? 'pointer' : 'default',
    opacity: enabled ? 1 : 0.6,
    transition: 'all 0.2s',
    marginTop: 8,
  }),
  generateBtn: (enabled) => ({
    width: '100%',
    padding: '14px 0',
    borderRadius: 12,
    border: 'none',
    background: enabled ? 'rgba(102,126,234,0.3)' : 'rgba(255,255,255,0.05)',
    color: enabled ? '#fff' : 'rgba(255,255,255,0.3)',
    fontSize: 14,
    fontWeight: 600,
    cursor: enabled ? 'pointer' : 'default',
    marginBottom: 16,
  }),
  skipLink: {
    display: 'block',
    textAlign: 'center',
    fontSize: 13,
    marginTop: 16,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    color: 'rgba(255, 255, 255, 0.5)',
    textDecoration: 'underline',
  },
  emptyState: {
    textAlign: 'center',
    padding: '32px 16px',
    color: 'rgba(255, 255, 255, 0.65)',
    fontSize: 14,
  },
  loading: {
    textAlign: 'center',
    padding: 40,
    color: 'rgba(255, 255, 255, 0.65)',
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ArtistPickStep({ userId, onComplete }) {
  const [activeTab, setActiveTab]           = useState('existing');
  const [creators, setCreators]             = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [inviteUrl, setInviteUrl]           = useState(null);
  const [invitedHandle, setInvitedHandle]   = useState('');
  const [loading, setLoading]               = useState(true);
  const [actionLoading, setActionLoading]   = useState(false);

  // Fetch creators on mount
  useEffect(() => {
    authFetch(`${API_BASE}/api/nominations/creators`)
      .then(r => {
        if (!r.ok) {
          console.warn('[ArtistPickStep] creators fetch failed:', r.status);
          return { creators: [] };
        }
        return r.json();
      })
      .then(data => {
        console.log('[ArtistPickStep] creators response:', data);
        const list = data.creators || [];
        setCreators(list);
        setLoading(false);
        // If no creators exist, auto-switch to the Invite tab
        if (list.length === 0) {
          setActiveTab('nominate');
        }
      })
      .catch(err => {
        console.error('[ArtistPickStep] creators fetch error:', err);
        setLoading(false);
        setActiveTab('nominate');
      });
  }, []);

  // ── Tab 1: Recruit existing creator ─────────────────────────────────────────
  const handleRecruitContinue = useCallback(async () => {
    if (!selectedCreator || actionLoading) return;
    setActionLoading(true);

    try {
      // Recruit via the server route (Prompt 1.5). It uses the same canonical
      // key as every other recruit path — recruit_creator_{recruiter}_{creator}
      // — so this pick and a later profile-page recruit can never both pay.
      await authFetch(`${API_BASE}/api/creators/${encodeURIComponent(selectedCreator.user_id)}/recruit`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ source: 'artist_pick_onboarding' }),
      });
    } catch (err) {
      console.warn('[ArtistPickStep] recruit award failed silently:', err);
    }

    setActionLoading(false);
    onComplete({ recruited: selectedCreator.artist_name });
  }, [selectedCreator, onComplete, actionLoading]);

  // ── Tab 2: InviteArtistPanel callback ─────────────────────────────────────
  const handleNominateContinue = useCallback(() => {
    onComplete({ nominated: invitedHandle });
  }, [onComplete, invitedHandle]);

  const handleSkip = useCallback(() => {
    onComplete({});
  }, [onComplete]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Pick Your First Artist</h1>
        <p style={styles.subtitle}>
          Recruit a creator to your street team — or invite one to join.
        </p>
      </div>

      {/* Tabs */}
      <div style={styles.tabRow}>
        <button
          style={styles.tab(activeTab === 'existing')}
          onClick={() => setActiveTab('existing')}
        >
          Artists on Big Creative
        </button>
        <button
          style={styles.tab(activeTab === 'nominate')}
          onClick={() => setActiveTab('nominate')}
        >
          Invite your favorite
        </button>
      </div>

      {/* Tab 1: Existing creators */}
      {activeTab === 'existing' && (
        <>
          {loading ? (
            <div style={styles.loading}>Loading artists...</div>
          ) : creators.length === 0 ? (
            <div style={styles.emptyState}>
              No artists yet — use the Invite tab to bring your favorite artist in.
            </div>
          ) : (
            <div style={styles.grid}>
              {creators.map(c => (
                <div
                  key={c.user_id}
                  style={styles.card(selectedCreator?.user_id === c.user_id)}
                  onClick={() => setSelectedCreator(c)}
                >
                  {c.avatar_url ? (
                    <img src={c.avatar_url} alt={c.artist_name} style={styles.avatar} />
                  ) : (
                    <div style={styles.initialsCircle}>
                      {getInitials(c.artist_name)}
                    </div>
                  )}
                  <p style={styles.cardName}>{c.artist_name || 'Unnamed'}</p>
                  {c.genre && <p style={styles.cardMeta}>{c.genre}</p>}
                  {c.stage && c.stage !== 'Raw' && (
                    <span style={styles.stageBadge}>{c.stage}</span>
                  )}
                  <p style={styles.fanCount}>
                    {c.total_fans || 0} fan{(c.total_fans || 0) !== 1 ? 's' : ''}
                  </p>
                </div>
              ))}
            </div>
          )}

          <button
            style={styles.continueBtn(!!selectedCreator)}
            onClick={handleRecruitContinue}
            disabled={!selectedCreator || actionLoading}
          >
            {actionLoading
              ? 'Recruiting...'
              : selectedCreator
                ? `Recruit ${selectedCreator.artist_name} — Continue`
                : 'Continue'}
          </button>
        </>
      )}

      {/* Tab 2: Nominate / invite — uses shared InviteArtistPanel */}
      {activeTab === 'nominate' && (
        <>
          <InviteArtistPanel
            userId={userId}
            compact
            onInviteComplete={({ handle: h, inviteUrl: url }) => {
              setInviteUrl(url);
              setInvitedHandle(h);
            }}
          />

          <button
            style={styles.continueBtn(!!inviteUrl)}
            onClick={handleNominateContinue}
            disabled={!inviteUrl}
          >
            {inviteUrl ? 'Link ready — Continue' : 'Generate a link to continue'}
          </button>
        </>
      )}

      {/* Skip */}
      <button style={styles.skipLink} onClick={handleSkip}>
        Skip for now
      </button>
    </div>
  );
}
